import { Project, NPMPackageInfo } from '../types';

interface PackageJSONConfig {
    name: string;
    version: string;
    private: boolean;
    type: string;
    scripts: Record<string, string>;
    dependencies: Record<string, string>;
    devDependencies: Record<string, string>;
    peerDependencies?: Record<string, string>;
}

type DependencyType = 'dependencies' | 'devDependencies' | 'peerDependencies';

interface OrganizedPackages {
    dependencies: Record<string, string>;
    devDependencies: Record<string, string>;
    peerDependencies: Record<string, string>;
}

export class PackageJSONGenerator {
    private project: Project;
    private mode: 'create' | 'update' | 'preserve';

    // Essential packages that should always be present in a React project
    private static readonly ESSENTIAL_DEPENDENCIES = {
        "react": "^18.2.0",
        "react-dom": "^18.2.0",
        "@dfinity/agent": "^2.4.0",
        "@dfinity/assets": "^2.4.0",
        "@dfinity/auth-client": "^2.4.0",
        "@dfinity/candid": "^2.4.1",
        "@dfinity/principal": "^2.4.1",
        "@dfinity/ic-management": "^6.0.4",
        "@dfinity/identity": "^2.4.0",
        "@dfinity/utils": "^2.10.0",
        "tailwindcss": "^3.4.0",
        "postcss": "^8.4.31",
        "autoprefixer": "^10.4.16"
    };

    private static readonly ESSENTIAL_DEV_DEPENDENCIES = {
        "@types/react": "^18.2.0",
        "@types/react-dom": "^18.2.0",
        "@vitejs/plugin-react": "^4.2.0",
        "typescript": "5.1.6",
        "vite": "^4.4.12",
    };

    constructor(project: Project, mode: 'create' | 'update' | 'preserve' = 'update') {
        this.project = project;
        this.mode = mode;
    }

    /**
     * Normalize dependency type to ensure it's valid
     */
    private normalizeDependencyType(dependencyType: string): DependencyType {
        switch (dependencyType) {
            case 'dependencies':
            case 'devDependencies':
            case 'peerDependencies':
                return dependencyType as DependencyType;
            default:
                console.warn(`[PackageJSONGenerator] Unknown dependency type '${dependencyType}', defaulting to 'dependencies'`);
                return 'dependencies';
        }
    }

    /**
     * Main method to generate package.json content
     */
    public generatePackageJSON(): string {
        console.log(`[PackageJSONGenerator] Generating package.json in '${this.mode}' mode`);

        const config = this.buildPackageConfig();

        // Validate the configuration before returning
        this.validateConfig(config);

        return JSON.stringify(config, null, 2);
    }

    /**
     * Build the complete package.json configuration
     */
    private buildPackageConfig(): PackageJSONConfig {
        const projectPackages = this.getProjectPackages();
        const dependencies = this.resolveDependencies(projectPackages);

        return {
            name: this.generatePackageName(),
            version: "0.1.0",
            private: true,
            type: "commonjs",
            scripts: this.getScripts(),
            dependencies: dependencies.dependencies,
            devDependencies: dependencies.devDependencies,
            ...(Object.keys(dependencies.peerDependencies).length > 0 && {
                peerDependencies: dependencies.peerDependencies
            })
        };
    }

    /**
     * Extract and organize packages from the project
     */
    private getProjectPackages(): OrganizedPackages {
        if (!this.project.npmPackages || !this.project.npmPackages[0]) {
            console.log('[PackageJSONGenerator] No npm packages found in project');
            return {
                dependencies: {},
                devDependencies: {},
                peerDependencies: {}
            };
        }

        return this.organizePackagesByType(this.project.npmPackages[0]);
    }

    /**
     * Organize packages by dependency type, handling duplicates with priority
     */
    private organizePackagesByType(packages: NPMPackageInfo[]): OrganizedPackages {
        const result: OrganizedPackages = {
            dependencies: {},
            devDependencies: {},
            peerDependencies: {}
        };

        // Priority order: dependencies > devDependencies > peerDependencies
        const priorityOrder: Record<DependencyType, number> = {
            'dependencies': 1,
            'devDependencies': 2,
            'peerDependencies': 3
        };

        const packageMap = new Map<string, { version: string; dependencyType: DependencyType; priority: number }>();

        // Process packages and handle duplicates
        packages.forEach(pkg => {
            const { name, version, dependencyType } = pkg;

            // Validate and normalize the dependency type
            const normalizedType = this.normalizeDependencyType(dependencyType);
            const currentPriority = priorityOrder[normalizedType];

            if (packageMap.has(name)) {
                const existing = packageMap.get(name)!;

                // Only replace if current has higher priority (lower number)
                if (currentPriority < existing.priority) {
                    console.log(`[PackageJSONGenerator] Replacing ${name}: ${existing.dependencyType} -> ${normalizedType}`);
                    packageMap.set(name, { version, dependencyType: normalizedType, priority: currentPriority });
                } else {
                    console.log(`[PackageJSONGenerator] Keeping ${name} in ${existing.dependencyType}, ignoring ${normalizedType}`);
                }
            } else {
                packageMap.set(name, { version, dependencyType: normalizedType, priority: currentPriority });
            }
        });

        // Convert map to organized structure
        packageMap.forEach(({ version, dependencyType }, name) => {
            result[dependencyType][name] = version;
        });

        return result;
    }

    /**
     * Resolve final dependencies based on mode and project state
     */
    private resolveDependencies(projectPackages: OrganizedPackages): OrganizedPackages {
        switch (this.mode) {
            case 'create':
                return this.handleCreateMode(projectPackages);

            case 'preserve':
                return this.handlePreserveMode(projectPackages);

            case 'update':
            default:
                return this.handleUpdateMode(projectPackages);
        }
    }

    /**
     * Handle create mode - new package.json with essentials + project packages
     */
    private handleCreateMode(projectPackages: OrganizedPackages): OrganizedPackages {
        console.log('[PackageJSONGenerator] Create mode: Adding essential dependencies');

        return {
            dependencies: {
                ...PackageJSONGenerator.ESSENTIAL_DEPENDENCIES,
                ...projectPackages.dependencies
            },
            devDependencies: {
                ...PackageJSONGenerator.ESSENTIAL_DEV_DEPENDENCIES,
                ...projectPackages.devDependencies
            },
            peerDependencies: projectPackages.peerDependencies
        };
    }

    /**
     * Handle preserve mode - keep existing structure, don't add essentials
     */
    private handlePreserveMode(projectPackages: OrganizedPackages): OrganizedPackages {
        console.log('[PackageJSONGenerator] Preserve mode: Using only project packages');
        return projectPackages;
    }

    /**
     * Handle update mode - smart merging with fallback to essentials
     */
    private handleUpdateMode(projectPackages: OrganizedPackages): OrganizedPackages {
        const hasAnyDependencies =
            Object.keys(projectPackages.dependencies).length > 0 ||
            Object.keys(projectPackages.devDependencies).length > 0;

        if (!hasAnyDependencies) {
            console.log('[PackageJSONGenerator] Update mode: No project packages found, using essentials');

            // If no packages found, check if this looks like a React project
            if (this.isReactProject()) {
                return {
                    dependencies: { ...PackageJSONGenerator.ESSENTIAL_DEPENDENCIES },
                    devDependencies: { ...PackageJSONGenerator.ESSENTIAL_DEV_DEPENDENCIES },
                    peerDependencies: {}
                };
            } else {
                // Non-React project, return minimal structure
                return {
                    dependencies: {},
                    devDependencies: {},
                    peerDependencies: {}
                };
            }
        }

        console.log('[PackageJSONGenerator] Update mode: Using project packages with smart defaults');

        // 🚨 FIXED: Always merge essentials first, then project packages override
        const result: OrganizedPackages = {
            dependencies: {
                ...PackageJSONGenerator.ESSENTIAL_DEPENDENCIES,
                ...projectPackages.dependencies
            },
            devDependencies: {
                ...PackageJSONGenerator.ESSENTIAL_DEV_DEPENDENCIES,
                ...projectPackages.devDependencies
            },
            peerDependencies: projectPackages.peerDependencies
        };

        // 🗑️ REMOVED: Conditional logic that could skip essentials
        // The essentials are now always included above, so no need for conditional checks

        return result;
    }

    /**
     * Check if this appears to be a React project
     */
    private isReactProject(): boolean {
        const projectName = this.project.name.toLowerCase();

        // Check project name patterns
        if (projectName.includes('react') || projectName.includes('frontend')) {
            return true;
        }

        // Could add more heuristics here like checking file extensions
        // or existing project structure

        return true; // Default to React for ICP Studio projects
    }

    /**
     * Check if project has TypeScript files
     */
    private hasTypeScriptFiles(): boolean {
        // This could be enhanced to actually check project files
        // For now, assume TypeScript is used
        return true;
    }

    /**
     * Generate the package name based on project name
     */
    private generatePackageName(): string {
        const baseName = this.project.name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, ''); // Remove invalid characters

        return `${baseName}-frontend`;
    }

    /**
     * Get the default scripts configuration
     */
    private getScripts(): Record<string, string> {
        return {
            "dev": "vite",
            "build": "vite build",
            "preview": "vite preview"
        };
    }

    /**
     * Validate the generated configuration
     */
    private validateConfig(config: PackageJSONConfig): void {
        if (!config.name) {
            throw new Error('Package name is required');
        }

        if (!config.version) {
            throw new Error('Package version is required');
        }

        if (!config.scripts || Object.keys(config.scripts).length === 0) {
            throw new Error('At least one script is required');
        }

        console.log(`[PackageJSONGenerator] Generated valid package.json for '${config.name}'`);
    }

    /**
     * Static method to create a generator with specific mode
     */
    public static create(project: Project): PackageJSONGenerator {
        return new PackageJSONGenerator(project, 'create');
    }

    public static update(project: Project): PackageJSONGenerator {
        return new PackageJSONGenerator(project, 'update');
    }

    public static preserve(project: Project): PackageJSONGenerator {
        return new PackageJSONGenerator(project, 'preserve');
    }
}